import {
  IPushBulkRequeueBody,
  IPushBulkRequeueResult,
  IPushMarkPushedBody,
  IPushRequeueBody,
  IPushStateTotal,
  IPushTransaction,
  IPaginate,
  RootState,
} from '@/types/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { DownstreamPushService } from '@/services/downstream-push.service';

const state = {
  transactions: [] as IPushTransaction[],
  pagination: {
    total: 0,
    itemsPerPage: 50,
    page: 1,
    pages: 0,
  } as IPaginate,
  stateTotals: [] as IPushStateTotal[],
  maxPushAttempts: 5,
  // Result of the last bulk requeue call (dry run or live), for the dialog.
  bulkResult: null as IPushBulkRequeueResult | null,
};

type DownstreamPushState = typeof state;

const mutations: MutationTree<DownstreamPushState> = {
  UPDATE_TRANSACTIONS(state, payload: Array<IPushTransaction>) {
    state.transactions = payload;
  },
  UPDATE_PAGINATION(state, payload: IPaginate) {
    state.pagination = payload;
  },
  UPDATE_STATE_TOTALS(state, payload: Array<IPushStateTotal>) {
    state.stateTotals = payload;
  },
  UPDATE_MAX_PUSH_ATTEMPTS(state, payload: number) {
    state.maxPushAttempts = payload;
  },
  UPDATE_BULK_RESULT(state, payload: IPushBulkRequeueResult | null) {
    state.bulkResult = payload;
  },
};

const actions: ActionTree<DownstreamPushState, RootState> = {
  async fetchTransactions({ dispatch, commit }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await DownstreamPushService.fetchTransactions(query);
      if (response$) {
        dispatch('isPageLoading', false, { root: true });
        // buildApiResponse envelope: { statusCode, statusMessage, data: { transactions, meta } }
        const { transactions, meta } = response$.data.data;
        commit('UPDATE_TRANSACTIONS', transactions);
        commit('UPDATE_STATE_TOTALS', meta.byState ?? []);
        commit('UPDATE_MAX_PUSH_ATTEMPTS', meta.maxPushAttempts ?? 5);
        commit('UPDATE_PAGINATION', {
          page: meta.page,
          pages: meta.totalPages,
          total: meta.total,
          itemsPerPage: meta.limit,
        });
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response?.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async resolveTransaction(
    { dispatch },
    payload: {
      adminId: string;
      transactionId: string;
      action: 'requeue' | 'mark-pushed';
      reason?: string;
      confirmedNotPushed?: boolean;
      overridePushFloor?: boolean;
      query: string;
    },
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      let message: string;
      if (payload.action === 'mark-pushed') {
        const body: IPushMarkPushedBody = { reason: payload.reason as string };
        await DownstreamPushService.markPushed(payload.adminId, payload.transactionId, body);
        message = 'Transaction recorded as pushed — nothing was sent downstream';
      } else {
        const body: IPushRequeueBody = {
          reason: payload.reason,
          confirmedNotPushed: payload.confirmedNotPushed,
          overridePushFloor: payload.overridePushFloor,
        };
        await DownstreamPushService.requeue(payload.adminId, payload.transactionId, body);
        message = 'Requeued — the sweep will retry it within 30 seconds';
      }
      dispatch('isLoading', false, { root: true });
      dispatch('updateDialog', { idx: 'resolve', state: false }, { root: true });
      dispatch('snackBarMessage', message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchTransactions', payload.query);
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response?.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async bulkRequeue({ dispatch, commit }, payload: { adminId: string; body: IPushBulkRequeueBody; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await DownstreamPushService.bulkRequeue(payload.adminId, payload.body);
      const result = response$.data?.data as IPushBulkRequeueResult;
      commit('UPDATE_BULK_RESULT', result);
      dispatch('isLoading', false, { root: true });
      if (!result.dryRun) {
        dispatch(
          'snackBarMessage',
          `Requeued ${result.requeued} of ${result.matched} matched ${
            result.matched === 1 ? 'transaction' : 'transactions'
          }${result.skipped.length ? ` (${result.skipped.length} skipped)` : ''}`,
          { root: true },
        );
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('fetchTransactions', payload.query);
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response?.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  clearBulkResult({ commit }) {
    commit('UPDATE_BULK_RESULT', null);
  },
};

const getters: GetterTree<DownstreamPushState, RootState> = {
  getTransactions: state => state.transactions,
  getPagination: state => state.pagination,
  getStateTotals: state => state.stateTotals,
  getMaxPushAttempts: state => state.maxPushAttempts,
  getBulkResult: state => state.bulkResult,
};

export const downstreamPush: Module<DownstreamPushState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
